import { apiClient } from '../config/api';

// FEAT-045 — Caregiver Sign-Up Stepper API client (web).
// MVP-8.10, MVP-8.11, MVP-18.17.
//
// Server-side state powers the resumable stepper. The same five step
// slugs are the canonical allow-list across the codebase; the web
// component imports CAREGIVER_ONBOARDING_STEPS from this module so the
// stepper UI and the API contract stay in lockstep.

export const CAREGIVER_ONBOARDING_STEPS = [
  'profile_details',
  'credentials',
  'learning',
  'platform_rules',
  'disclaimer',
] as const;

export type CaregiverOnboardingStep = (typeof CAREGIVER_ONBOARDING_STEPS)[number];

export interface CaregiverOnboardingProgress {
  currentStep: CaregiverOnboardingStep;
  completedSteps: CaregiverOnboardingStep[];
  completedAt: string | null;
  nextStep: CaregiverOnboardingStep | null;
  completionPercent: number;
  isComplete: boolean;
  missing: CaregiverOnboardingStep[];
}

interface ProgressEnvelope {
  success: boolean;
  data: CaregiverOnboardingProgress;
  timestamp: string;
}

class CaregiverOnboardingService {
  async getProgress(): Promise<CaregiverOnboardingProgress> {
    const response = await apiClient.get<ProgressEnvelope>(
      '/caregiver-onboarding/me',
    );
    return response.data.data;
  }

  async advance(step: CaregiverOnboardingStep): Promise<CaregiverOnboardingProgress> {
    const response = await apiClient.post<ProgressEnvelope>(
      '/caregiver-onboarding/me/advance',
      { step },
    );
    return response.data.data;
  }
}

export const caregiverOnboardingService = new CaregiverOnboardingService();

export const CAREGIVER_ONBOARDING_STEP_LABELS: Record<CaregiverOnboardingStep, string> = {
  profile_details: 'Tell families about you',
  credentials: 'Share your credentials',
  learning: 'Careva learning',
  platform_rules: 'How we keep Careva warm',
  disclaimer: 'Your independence',
};

export const CAREGIVER_ONBOARDING_STEP_HINTS: Record<CaregiverOnboardingStep, string> = {
  profile_details: 'A short bio and a friendly photo go a long way.',
  credentials: 'Upload a document or share a state license number.',
  learning: 'A few short lessons help you feel ready on day one.',
  platform_rules: "A few promises that keep families safe and supported.",
  disclaimer: 'A simple note about your role as an independent caregiver.',
};
